/**
 * Iter 253c · HR admin page — review employee profile edit requests.
 *
 * Two tabs: Pending (approve/reject) + History (approved/rejected).
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Users, CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { apiErrorMessage } from "@/lib/apiError";

export default function HRProfileEditsAdmin() {
  const { activeWorkspaceId } = useAuth();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const [pRes, hRes1, hRes2] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/hr/profile-edits`, { params: { status: "pending" } }),
        api.get(`/workspaces/${activeWorkspaceId}/hr/profile-edits`, { params: { status: "approved" } }),
        api.get(`/workspaces/${activeWorkspaceId}/hr/profile-edits`, { params: { status: "rejected" } }),
      ]);
      setPending(pRes.data || []);
      const merged = [...(hRes1.data || []), ...(hRes2.data || [])].sort(
        (a, b) => (b.reviewed_at || "").localeCompare(a.reviewed_at || ""),
      );
      setHistory(merged);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load"));
    } finally { setLoading(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  // Iter 253z-13 · Auto-refresh every 30 seconds so HR sees new
  // submissions without a manual reload (fixes: "employee submitted the
  // profile edit but did not reach HR for approval").
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const t = setInterval(() => { load(); }, 30000);
    return () => clearInterval(t);
  }, [activeWorkspaceId, load]);

  const approve = async (edit) => {
    if (!window.confirm(`Approve ${Object.keys(edit.changes || {}).length} change(s) for ${edit.user_name}?`)) return;
    try {
      await api.post(`/hr/profile-edits/${edit.id}/approve`, { note: null });
      toast.success("Approved");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Approve failed"));
    }
  };

  const openReject = (edit) => {
    setRejectingId(edit.id);
    setRejectNote("");
  };

  const submitReject = async () => {
    if (!rejectNote.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post(`/hr/profile-edits/${rejectingId}/reject`, { note: rejectNote.trim() });
      toast.success("Rejected");
      setRejectingId(null); setRejectNote("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reject failed"));
    }
  };

  const renderRow = (edit, actionsMode) => (
    <div key={edit.id} className="border border-border p-3 space-y-2" data-testid={`profile-edit-row-${edit.id}`}>
      <div className="flex items-center gap-2">
        <div className="text-sm font-bold flex-1">
          {edit.user_name} <span className="text-muted-foreground font-normal">· {edit.user_email}</span>
        </div>
        {edit.status === "pending" && <span className="text-[10px] px-2 py-0.5 border border-amber-500 bg-amber-50 text-amber-800 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>}
        {edit.status === "approved" && <span className="text-[10px] px-2 py-0.5 border border-emerald-500 bg-emerald-50 text-emerald-800 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approved</span>}
        {edit.status === "rejected" && <span className="text-[10px] px-2 py-0.5 border border-rose-500 bg-rose-50 text-rose-800 flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected</span>}
      </div>
      <div className="text-[11px] text-muted-foreground">
        Submitted {edit.submitted_at?.slice(0, 16)}{edit.reviewed_at ? ` · Reviewed ${edit.reviewed_at?.slice(0, 16)} by ${edit.reviewed_by_name}` : ""}
      </div>
      <table className="w-full text-xs border border-border" data-testid={`profile-edit-diff-${edit.id}`}>
        <thead className="bg-muted/40">
          <tr>
            <th className="px-2 py-1 text-left w-40">Field</th>
            <th className="px-2 py-1 text-left">Current</th>
            <th className="px-2 py-1 text-left">Proposed</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(edit.changes || {}).map(([k, v]) => (
            <tr key={k} className="border-t border-border">
              <td className="px-2 py-1 font-medium">{k}</td>
              <td className="px-2 py-1 text-muted-foreground">
                {typeof edit.current_snapshot?.[k] === "string" && edit.current_snapshot[k].startsWith("data:image")
                  ? <img src={edit.current_snapshot[k]} alt="" className="h-8 w-8 rounded-full border border-border" />
                  : (String(edit.current_snapshot?.[k] ?? "—").slice(0, 200) || "—")}
              </td>
              <td className="px-2 py-1 text-primary font-medium">
                {typeof v === "string" && v.startsWith("data:image")
                  ? <img src={v} alt="" className="h-8 w-8 rounded-full border border-primary" />
                  : String(v ?? "").slice(0, 200)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {edit.review_note && (
        <div className={`text-[11px] italic border-l-2 pl-2 ${edit.status === "rejected" ? "border-rose-500 text-rose-700" : "border-emerald-500 text-emerald-700"}`}>
          Note: {edit.review_note}
        </div>
      )}
      {actionsMode === "pending" && (
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={() => openReject(edit)}
                   className="text-xs px-3 py-1.5 border border-rose-500 text-rose-600 hover:bg-rose-50"
                   data-testid={`profile-edit-reject-${edit.id}`}>
            Reject
          </button>
          <button type="button" onClick={() => approve(edit)}
                   className="text-xs px-3 py-1.5 border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600"
                   data-testid={`profile-edit-approve-${edit.id}`}>
            Approve &amp; apply
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4" data-testid="hr-profile-edits-page">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-primary" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">HR · Profile edit approvals</h1>
          <p className="text-sm text-muted-foreground">Review changes employees have proposed to their personal information. Auto-refreshes every 30 seconds.</p>
        </div>
        <button type="button" onClick={load}
                 className="text-xs px-3 py-1.5 border border-border hover:bg-muted inline-flex items-center gap-1.5"
                 data-testid="hr-pe-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="rounded-none">
          <TabsTrigger value="pending" className="rounded-none" data-testid="hr-pe-tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="history" className="rounded-none" data-testid="hr-pe-tab-history">History ({history.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="pt-4 space-y-3">
          {loading ? <div className="text-xs text-muted-foreground">Loading…</div>
           : pending.length === 0
             ? <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground" data-testid="pe-empty">No pending profile edits.</div>
             : pending.map((e) => renderRow(e, "pending"))}
        </TabsContent>
        <TabsContent value="history" className="pt-4 space-y-3">
          {loading ? <div className="text-xs text-muted-foreground">Loading…</div>
           : history.length === 0
             ? <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">No history yet.</div>
             : history.map((e) => renderRow(e, "history"))}
        </TabsContent>
      </Tabs>

      {rejectingId && (
        <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center" data-testid="reject-dialog">
          <div className="bg-background border border-border p-4 w-[400px] space-y-3 shadow-lg">
            <div className="text-sm font-bold">Reject profile edit</div>
            <p className="text-xs text-muted-foreground">Please give the employee a clear reason. This message will be shown on their profile.</p>
            <textarea rows={3} value={rejectNote}
                         onChange={(e) => setRejectNote(e.target.value)}
                         className="w-full text-sm border border-border p-2"
                         placeholder="e.g. National ID format is wrong — please share XX-YYYY-ZZ"
                         data-testid="reject-note-input" />
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setRejectingId(null)}
                       className="px-3 py-1.5 border border-border hover:bg-muted text-xs"
                       data-testid="reject-cancel-btn">Cancel</button>
              <button type="button" onClick={submitReject}
                       className="px-3 py-1.5 border border-rose-500 bg-rose-500 text-white hover:bg-rose-600 text-xs"
                       data-testid="reject-submit-btn">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
